import { getSupabaseClient } from '../supabase';
import type { Profile, OnboardingStep } from '@traction/shared';

export async function getProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await getSupabaseClient()
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data as Profile;
}

export async function createProfile(
  userId: string,
  profile: Partial<Profile>
): Promise<Profile> {
  const insertData = {
    user_id: userId,
    full_name: profile.full_name ?? '',
    onboarding_step: 'account',
    tone_of_voice: 'friendly',
    target_industries: [],
    target_company_sizes: [],
    target_roles: [],
    target_geography: [],
    target_funding_stages: [],
    target_seniorities: [],
    target_keywords: [],
    target_technologies: [],
    target_revenue_ranges: [],
    target_departments: [],
    exclude_titles: [],
    require_email: false,
    hiring_intent: false,
    company_industry: [],
    preferred_send_times: [],
    ...profile,
  };

  const { data, error } = await getSupabaseClient()
    .from('profiles')
    .insert(insertData)
    .select()
    .single();

  if (error) throw error;
  return data as Profile;
}

export async function updateProfile(
  userId: string,
  updates: Partial<Profile>
): Promise<Profile> {
  const supabase = getSupabaseClient();

  // Ensure user exists first (required for foreign key constraint)
  await supabase
    .from('users')
    .upsert({ id: userId, email: updates.full_name ? `${userId}@firebase.local` : `${userId}@firebase.local` }, { onConflict: 'id' })
    .select()
    .single();

  // Use upsert to create profile if it doesn't exist
  const upsertData = {
    user_id: userId,
    full_name: updates.full_name ?? '',
    onboarding_step: 'account',
    tone_of_voice: 'friendly',
    target_industries: [],
    target_company_sizes: [],
    target_roles: [],
    target_geography: [],
    target_funding_stages: [],
    target_seniorities: [],
    target_keywords: [],
    target_technologies: [],
    target_revenue_ranges: [],
    target_departments: [],
    exclude_titles: [],
    require_email: false,
    hiring_intent: false,
    company_industry: [],
    preferred_send_times: [],
    ...updates,
  };

  const { data, error } = await supabase
    .from('profiles')
    .upsert(upsertData, { onConflict: 'user_id' })
    .select()
    .single();

  if (error) throw error;
  return data as Profile;
}

export async function updateOnboardingStep(
  userId: string,
  step: OnboardingStep
): Promise<void> {
  const updates: Record<string, any> = { onboarding_step: step };

  if (step === 'completed') {
    updates.onboarding_completed_at = new Date().toISOString();
  }

  const { error } = await getSupabaseClient()
    .from('profiles')
    .update(updates)
    .eq('user_id', userId);

  if (error) throw error;
}
